<script setup lang="ts">
    import { nextTick, ref } from 'vue';
    import { __ } from '../../stores/expressions';
    import Modal, { ModalSize } from '../Modal.vue';
    import { useAskMeStore } from '../../stores/ask-me';
    import { IChatMessage } from '@/js/services/api/chat/chat';
    import { useRoute } from 'vue-router';
    import EchoButton from '../EchoButton.vue';
    import { StarIcon } from '@lucide/vue';
    import IconButton from '../IconButton.vue';

    const route = useRoute();

    const props = defineProps<{ message: IChatMessage }>();

    const commentTextarea = ref<HTMLTextAreaElement | null>(null);

    const ask_me = useAskMeStore();

    const show_modal = ref(false);

    const rating = ref<number>(0);

    const comment = ref<string>('');

    const disabled = ref(false);

    const reset = () => {
        disabled.value = false;
        rating.value = 0;
        comment.value = '';
        show_modal.value = false;
    };

    const rememberRating = (score: number) => {
        rating.value = score;

        nextTick(() => {
            commentTextarea.value?.focus();
        });
    };

    const rate = () => {
        show_modal.value = false;

        if (disabled.value || rating.value === 0) {
            return;
        }

        disabled.value = true;

        ask_me
            .rateMessage(
                route.params.chat as string,
                props.message.id as string,
                rating.value,
                comment.value
            )
            .then(() => {
                reset();
            });
    };
</script>

<template>
    <template v-if="!message.isUserMessage">
        <IconButton
            size="size-4"
            area-size="size-5"
            class="custom-focus rounded-full border border-gray-dark"
            :tooltip="__('rate-message-tooltip')"
            @click="show_modal = true"
        >
            <StarIcon class="size-4" />
        </IconButton>

        <Modal :show="show_modal" :size="ModalSize.Medium" @close="reset()">
            <h2 class="text-2xl mb-2 font-semibold">
                {{ __('rate-message-header') }}
            </h2>
            <p class="mb-12">
                {{ __('rate-message-text') }}
            </p>

            <ul class="flex justify-between mt-4">
                <li v-for="score in [1, 2, 3, 4, 5]" :key="score" class="w-1/5 px-4 mb-20">
                    <button
                        class="Icon-button focus:ring-0 focus:outline-gray-dark w-full h-auto aspect-square rounded-full flex items-center justify-center"
                        :class="
                            rating >= score ? 'bg-yellow text-white' : 'border border-gray-dark'
                        "
                        @click="rememberRating(score)"
                    >
                        <div class="w-5/6">
                            <StarIcon class="size-full" />
                        </div>
                        <div class="absolute text-gray-dark text-2xl font-semibold top-full mt-4">
                            {{ score }}
                        </div>
                    </button>
                </li>
            </ul>

            <label class="sr-only" for="commentTextarea">
                {{ __('rate-message-optional-comment') }}
            </label>
            <textarea
                id="commentTextarea"
                ref="commentTextarea"
                v-model="comment"
                class="TextArea w-full h-30 mb-3 custom-focus"
                :placeholder="__('rate-message-optional-comment')"
            />

            <div class="flex justify-end">
                <EchoButton variant="primary" :disabled="disabled || rating === 0" @click="rate()">
                    {{ __('send') }}
                </EchoButton>
            </div>
        </Modal>
    </template>
</template>
